import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { Form, FormColumn, FormColumns, FormRow } from '@/components/Form'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { type InventoryItemDialogViewModelBase } from '@/features/inventory/admin/inventory-item-dialog/InventoryItemDialogViewModelBase'
import { match } from '@/utils/pattern-match-util'
import { getSupportingItemSpecDisplayName } from '@/features/inventory/inventory-item-display'
import { getSupportingItemSpecKey } from '@/features/inventory/InventoryItem'
import {
  InventoryItemForm,
  InventoryItemPriceTypeField,
  InventoryItemSupportingItemsField,
  InventoryItemTypeField,
} from '@/features/inventory/admin/components/InventoryItemForm'
import { ManageSupportingItemsModalFormContainer } from '@/features/inventory/admin/components/InventoryItemForm/ManageSupportingItemsModalForm/ManageSupportingItemsModalFormContainer'
import { type FeeEditorViewModel } from '@/features/inventory/admin/inventory-item-dialog/FeeEditorViewModel'
import { type FlatEditorViewModel } from '@/features/inventory/admin/inventory-item-dialog/FlatEditorViewModel'
import { type MarkupEditorViewModel } from '@/features/inventory/admin/inventory-item-dialog/MarkupEditorViewModel'
import { type MarkdownEditorViewModel } from '@/features/inventory/admin/inventory-item-dialog/MarkdownEditorViewModel'
import { ActionToggle } from '@/components/ActionToggle'
import { ManageExtensionBillingInfoContainer } from '@/features/inventory/admin/components/InventoryItemForm/ManageExtensionBillingInfo/ManageExtensionBillingInfoContainer'
import { JobProgressionBillingModalContainer } from '@/features/inventory/admin/components/InventoryItemForm/ManageExtensionBillingInfo/JobProgressionBillingModal/JobProgressionBillingModalContainer'

export interface InventoryItemFormContainerProps {
  vm: InventoryItemDialogViewModelBase
  title: string
  submitText: string
  overridden?: boolean
  children?: ReactNode
  editing?: boolean
}

export const InventoryItemFormContainer = observer(
  function InventoryItemFormContainer({
    vm,
    submitText,
    title,
    overridden,
    children,
    editing,
  }: InventoryItemFormContainerProps) {
    const testId = 'inventory-item-modal'

    return (
      <InventoryItemForm
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        title={title}
        submitText={submitText}
        isSubmitPending={vm.submit.pending}
        testId={testId}
        modals={
          <>
            <ManageSupportingItemsModalFormContainer
              vm={vm.manageSupportingItemsVm}
              testId={testId}
            />
            <JobProgressionBillingModalContainer
              vm={vm.extensionVm.progressionBillingVm.modalVm}
              // The submit button reuses another dialog's ID, which tests may rely on.
              testId="manage-user-capacity"
              stepsTestId={testId}
            />
          </>
        }
      >
        <Form id="addItemForm" onSubmit={vm.submit}>
          {children}
          <InventoryItemTypeField
            isDefault={vm.isDefault}
            isTax={vm.isTax}
            isBookkeeping={vm.isBookkeeping}
            isConsultation={vm.isConsultation}
            isExtension={vm.isExtension}
            isDisabled={editing || overridden}
            onItemTypeChange={vm.setItemType}
            testId={testId}
          />
          <FormRow>
            <TextInputContainer
              autoFocus
              label="Service item"
              field="name"
              placeholder="e.g. Schedule D"
              model={vm}
              error={vm.validation.getError('name')}
              data-test={`${testId}-name-input`}
              help={vm.remainingCharactersIndicator.name}
              isDisabled={overridden}
            />
          </FormRow>

          {vm.isConsultation && (
            <FormRow>
              <NumericInputContainer
                label="Consultation length in minutes"
                field="consultationLength"
                help="The length of the consultation in minutes."
                error={vm.validation.getError('consultationLength')}
                model={vm}
                testId={`${testId}-consultation-length-input`}
              />
            </FormRow>
          )}

          <FormRow>
            <TextAreaContainer
              rows={4}
              label="Description"
              field="description"
              minLength={1}
              maxLength={280}
              help="A client friendly description for the item. Max 280 characters."
              model={vm}
              error={vm.validation.getError('description')}
              data-test={`${testId}-description-input`}
              isDisabled={overridden}
            />
          </FormRow>

          <FormRow>
            <NumericInputContainer
              label="Courtesy quantity"
              field="courtesyQuantity"
              help="Threshold before items counts towards the job price."
              error={vm.validation.getError('courtesyQuantity')}
              model={vm}
              testId={`${testId}-courtesy-amount-input`}
            />
          </FormRow>

          <FormRow>
            <ActionToggle
              label="Hide from Pro"
              description="The Pro will not be able to see this service item within the job specs or job scope change request flow."
              value={vm.hideFromPro}
              onChange={vm.toggleHideFromPro}
              testId={`${testId}-hide-from-pro-toggle`}
            />
          </FormRow>

          {vm.isBookkeeping && (
            <FormRow>
              <ActionToggle
                label="Recurring"
                description="In the context of bookkeeping, this item will be added to the job at the end of every cycle when we run out of items to complete."
                value={vm.recurring}
                onChange={vm.toggleRecurring}
                isDisabled={editing || overridden}
                testId={`${testId}-bookkeeping-recurring-toggle`}
              />
            </FormRow>
          )}

          {vm.isExtension && (
            <ManageExtensionBillingInfoContainer
              billingVm={vm.extensionVm}
              editing={editing}
              overridden={overridden}
              testId={testId}
            />
          )}

          {!editing && vm.useRegularPricing && (
            <InventoryItemPriceTypeField
              isFlatPriceType={vm.isFlatPriceType}
              itemPriceType={vm.itemPriceType}
              requiresFlatPricing={vm.requiresFlatPricing}
              onItemPriceTypeChange={vm.setItemPriceType}
              testId={testId}
            />
          )}

          {vm.useRegularPricing &&
            match(vm.itemPriceType, {
              Fee: () => (
                <FeeItemPriceInputsContainer
                  vm={vm.pricingVms.Fee}
                  testId={testId}
                />
              ),
              Flat: () => (
                <>
                  <FlatItemPriceInputsContainer
                    vm={vm.pricingVms.Flat}
                    itemVm={vm}
                    testId={testId}
                  />
                  {vm.isTax && (
                    <TaxReviewPriceInputsContainer vm={vm} testId={testId} />
                  )}
                </>
              ),
              Markup: () => (
                <MarkupPriceInputsContainer
                  vm={vm.pricingVms.Markup}
                  testId={testId}
                />
              ),
              Markdown: () => (
                <MarkdownPriceInputsContainer
                  vm={vm.pricingVms.Markdown}
                  testId={testId}
                />
              ),
            })}

          <InventoryItemSupportingItemsField
            supportingItems={vm.supportingItems.map((item) => ({
              key: getSupportingItemSpecKey(item.spec),
              label: getSupportingItemSpecDisplayName(item.spec),
              required: item.required,
            }))}
            onManage={vm.manageSupportingItemsVm.show}
          />
        </Form>
      </InventoryItemForm>
    )
  },
)

interface FeeItemPriceInputsContainerProps {
  vm: FeeEditorViewModel
  testId: string
}

const FeeItemPriceInputsContainer = observer(
  function FeeItemPriceInputsContainer({
    vm,
    testId,
  }: FeeItemPriceInputsContainerProps) {
    return (
      <FormRow>
        <FormColumns>
          <FormColumn>
            <NumericInputContainer
              precision={2}
              label="Fee ($)"
              field="fee"
              model={vm}
              error={vm.validation.getError('fee')}
              testId={`${testId}-price-input`}
              help="The fee to charge the client. Exempt from markup and markdown calculations."
            />
          </FormColumn>
        </FormColumns>
      </FormRow>
    )
  },
)

interface FlatItemPriceInputsContainerProps {
  vm: FlatEditorViewModel
  itemVm: InventoryItemDialogViewModelBase
  testId: string
}

const FlatItemPriceInputsContainer = observer(
  function FlatItemPriceInputsContainer({
    vm,
    itemVm,
    testId,
  }: FlatItemPriceInputsContainerProps) {
    return (
      <FormRow>
        <FormColumns>
          <FormColumn>
            <NumericInputContainer
              precision={2}
              label={
                itemVm.isTax ? 'Prep client price ($)' : 'Client price ($)'
              }
              field="clientAmount"
              model={vm}
              error={vm.validation.getError('clientAmount')}
              testId={`${testId}-price-input`}
              help={
                itemVm.isTax
                  ? 'The amount to charge the client for the tax prep.'
                  : 'The amount to charge the client.'
              }
            />
          </FormColumn>

          <FormColumn>
            <NumericInputContainer
              precision={2}
              label={itemVm.isTax ? 'Prep Pro payout ($)' : 'Pro payout ($)'}
              error={vm.validation.getError('proPayoutAmount')}
              field="proPayoutAmount"
              model={vm}
              testId={`${testId}-pro-price-input`}
              help={
                itemVm.isTax
                  ? 'The amount to pay the Pro for the tax prep.'
                  : 'The amount to pay the Pro.'
              }
            />
          </FormColumn>
        </FormColumns>
      </FormRow>
    )
  },
)

interface MarkupPriceInputsContainerProps {
  vm: MarkupEditorViewModel
  testId: string
}

const MarkupPriceInputsContainer = observer(
  function MarkupPriceInputsContainer({
    vm,
    testId,
  }: MarkupPriceInputsContainerProps) {
    return (
      <FormRow>
        <FormColumns>
          <FormColumn>
            <NumericInputContainer
              precision={2}
              label="Client markup (%)"
              field="clientMarkupRate"
              model={vm}
              error={vm.validation.getError('clientMarkupRate')}
              testId={`${testId}-price-input`}
              help="The rate by which to mark up the job price for the client. Applied to the total job price."
            />
          </FormColumn>

          <FormColumn>
            <NumericInputContainer
              precision={2}
              label="Pro payout markup (%)"
              error={vm.validation.getError('proPayoutMarkupRate')}
              field="proPayoutMarkupRate"
              model={vm}
              testId={`${testId}-pro-price-input`}
              help="The rate by which to mark up the Pro payout amount. Applied to the total Pro payout."
            />
          </FormColumn>
        </FormColumns>
      </FormRow>
    )
  },
)

interface MarkdownPriceInputsContainerProps {
  vm: MarkdownEditorViewModel
  testId: string
}

const MarkdownPriceInputsContainer = observer(
  function MarkdownPriceInputsContainer({
    vm,
    testId,
  }: MarkdownPriceInputsContainerProps) {
    return (
      <FormRow>
        <FormColumns>
          <FormColumn>
            <NumericInputContainer
              precision={2}
              label="Client markdown (%)"
              field="clientMarkdownRate"
              model={vm}
              error={vm.validation.getError('clientMarkdownRate')}
              testId={`${testId}-price-input`}
              help="The rate by which to mark down the job price for the client. Applied to the total job price."
            />
          </FormColumn>

          <FormColumn>
            <NumericInputContainer
              precision={2}
              label="Pro payout markdown (%)"
              error={vm.validation.getError('proPayoutMarkdownRate')}
              field="proPayoutMarkdownRate"
              model={vm}
              testId={`${testId}-pro-price-input`}
              help="The rate by which to mark down the Pro payout amount. Applied to the total Pro payout."
            />
          </FormColumn>
        </FormColumns>
      </FormRow>
    )
  },
)

interface TaxReviewPriceInputsContainerProps {
  vm: InventoryItemDialogViewModelBase
  testId: string
}

const TaxReviewPriceInputsContainer = observer(
  function TaxReviewPriceInputsContainer({
    vm,
    testId,
  }: TaxReviewPriceInputsContainerProps) {
    return (
      <FormRow>
        <FormColumns>
          <FormColumn>
            <NumericInputContainer
              precision={2}
              label="Review client price ($)"
              field="reviewPrice"
              model={vm}
              error={vm.validation.getError('reviewPrice')}
              testId={`${testId}-review-price-input`}
              help="The amount to charge the client for the review."
            />
          </FormColumn>

          <FormColumn>
            <NumericInputContainer
              precision={2}
              label="Review Pro payout ($)"
              error={vm.validation.getError('reviewPayout')}
              field="reviewPayout"
              model={vm}
              testId={`${testId}-review-pro-payout-input`}
              help="The amount to pay the Pro for the review."
            />
          </FormColumn>
        </FormColumns>
      </FormRow>
    )
  },
)
